import { ReactElement, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Skeleton,
	TextField,
	TextFieldProps,
	Typography,
} from "@mui/material";
import { Controller, FieldValues, Path, RegisterOptions, UseFormReturn } from "react-hook-form";
import { InfoIcon } from "../../assets/icons";
import FormFieldHint from "./FormFieldHint";

type DialogProps = {
	title: string;
	content: ReactElement;
};

interface Props<T extends FieldValues> extends Omit<TextFieldProps, "name" | "label" | "control"> {
	name: Path<T>;
	label: string;
	readOnly?: boolean;
	isLoading?: boolean;
	hintMessage?: string;
	afterChange?: (value: string) => void;
	form: UseFormReturn<T, null>;
	rules?: Omit<RegisterOptions<T, Path<T>>, "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs">;
	dialog?: DialogProps;
	measure?: string;
	isTypeNumber?: boolean;
	onlyPositiveNumbers?: boolean;
}

const sanitizeNumberValue = (value: string, onlyPositiveNumbers: boolean): string => {
	const normalizedValue = value.replace(",", ".");

	const isNegative = !onlyPositiveNumbers && normalizedValue.startsWith("-");
	const sanitizedValue = normalizedValue.replace(/[^\d.]/g, "");

	const [integerPart, ...decimalParts] = sanitizedValue.split(".");

	const numericValue = decimalParts.length > 0 ? `${integerPart}.${decimalParts.join("")}` : integerPart;

	return isNegative ? `-${numericValue}` : numericValue;
};

const isIncompleteNumber = (value: string): boolean => {
	return value === "-" || value === "." || value === "-.";
};

const isValidNumber = (value: string): boolean => {
	if (value === "" || isIncompleteNumber(value)) {
		return false;
	}

	return Number.isFinite(Number(value));
};

/**
 * FormTextField acts as a wrapper component for text fields used in conjunction with React Hook Form.
 *
 * @param name        the name of the field bound to this text field
 * @param label       the label of the text field
 * @param readOnly    if true, makes the field read-only
 * @param isLoading   if true, the component will render a skeleton element
 * @param hintMessage the optional hint message that may be rendered at the end of the field
 * @param form        the form reference
 * @param afterChange if defined, this callback is invoked right after internal onChange event
 * @param rules       optional validation rules (if undefined, the field will be optional)
 * @param dialog	   if true, an info icon is rendered at the end of the field. Clicking on it opens a dialog showing the content of the "info" prop
 * 		              the content to be shown in the dialog triggered by the "dialog" prop
 * @param measure     optional measure to add in the input at the end
 * @param isTypeNumber transform the input in type number
 * @param onlyPositiveNumbers if true, negative numbers are not allowed
 * @param rest
 * @returns           a TextField wrapped in a React Hook Form Control component
 */
const FormTextField = <T extends FieldValues>({
	form,
	name,
	rules,
	label,
	readOnly,
	isLoading,
	hintMessage,
	afterChange,
	dialog,
	measure,
	isTypeNumber,
	onlyPositiveNumbers = false,
	...rest
}: Props<T>): React.ReactElement => {
	const [openDialog, setOpenDialog] = useState(false);

	// Show "*" right after mandatory fields (i.e. those to which validation rules are applied).
	const isRequired = !!rules?.required;
	label = isRequired ? `${label} *` : label;

	if (isLoading) {
		return <Skeleton height="44px" variant="rounded" className="loading-field" />;
	}

	return (
		<Controller
			name={name}
			control={form.control}
			rules={rules}
			render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
				<>
					<TextField
						{...rest}
						value={value ?? ""}
						label={label}
						variant="outlined"
						size="small"
						error={error !== undefined}
						type="text"
						helperText={error?.message ?? <FormFieldHint text={hintMessage} />}
						slotProps={{
							...rest.slotProps,
							htmlInput: {
								...(rest.slotProps?.htmlInput || {}),
								inputMode: isTypeNumber ? "decimal" : undefined,
							},
							input: {
								...(rest.slotProps?.input || {}),
								readOnly,
								className: readOnly ? "read-only" : undefined,
								startAdornment: dialog && (
									<InputAdornment position="start">
										<IconButton onClick={() => setOpenDialog(true)} size="small">
											<InfoIcon color="info" />
										</IconButton>
									</InputAdornment>
								),
								endAdornment: measure && (
									<Typography
										variant="body2"
										color="textSecondary"
										style={{
											width: "160px",
											textAlign: "right",
											lineHeight: 1.05,
										}}
									>
										{measure}
									</Typography>
								),
							},
						}}
						// className={readOnly ? "read-only" : undefined}
						onBlur={() => {
							const currentValue = String(value ?? "");

							if (isTypeNumber && isIncompleteNumber(currentValue)) {
								onChange("");
								afterChange?.("");
							}

							onBlur();
						}}
						onChange={(event) => {
							const nextValue = isTypeNumber
								? sanitizeNumberValue(event.target.value, onlyPositiveNumbers)
								: event.target.value;

							onChange(nextValue);

							// Notify external components only when the numeric value is valid.
							if (!isTypeNumber || nextValue === "" || isValidNumber(nextValue)) {
								afterChange?.(nextValue);
							}

							if (form.formState.isSubmitted) {
								void form.trigger(name);
							}
						}}
					/>

					<Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
						<DialogTitle>{dialog?.title}</DialogTitle>
						<DialogContent>{dialog?.content}</DialogContent>
					</Dialog>
				</>
			)}
		/>
	);
};

export default FormTextField;
